import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faArrowLeft,
  faUpload,
  faChartBar,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import MainLayout from '../Layout/MainLayout';
import { useAuth } from '../../context/AuthContext';
import { loadAgentComponent } from '../../utils/componentLoader';

const AgentComponent = () => {
  const { agentId, componentName } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [component, setComponent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dynamicComponent, setDynamicComponent] = useState(null);

  useEffect(() => {
    fetchComponentData();
  }, [agentId, componentName]);

  const fetchComponentData = async () => {
    try {
      setLoading(true);
      
      // Try dynamic component loading first
      const dynamicResult = await loadAgentComponent(agentId, componentName, token);
      
      if (dynamicResult && dynamicResult.component) {
        setComponent(dynamicResult.metadata);
        setDynamicComponent(dynamicResult.component);
        setLoading(false);
        return;
      }
      
      // Fallback to original loading (backwards compatibility)
      const response = await fetch(`/api/agents/${agentId}/frontend/components/${componentName}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setComponent(data.component);
      }
    } catch (error) {
      console.error('Error fetching component data:', error);
    } finally {
      setLoading(false);
    }
  };

  const iconMapping = {
    'upload': faUpload,
    'chart-bar': faChartBar,
    'bot': faRobot
  };

  if (loading) {
    return (
      <MainLayout title="Loading Component...">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="text-4xl text-blue-500 animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading component...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!component) {
    return (
      <MainLayout title="Component Not Found">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">Component not found</p>
            <button
              onClick={() => navigate(`/agents/${agentId}`)}
              className="btn btn-primary"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Back to Agent Dashboard
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title={component.displayName}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                <FontAwesomeIcon 
                  icon={iconMapping[component.icon] || faRobot} 
                  className="text-3xl" 
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold mb-2">{component.displayName}</h1>
                <p className="opacity-90">{component.description}</p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/agents/${agentId}`)}
              className="btn bg-white/20 hover:bg-white/30 text-white border-white/30"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Back
            </button>
          </div>
        </motion.div>

        {/* Component Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {component.displayName}
            </h3>
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              {component.type}
            </span>
          </div>
          <div className="card-body">
            {/* Dynamic Component Content */}
            {dynamicComponent ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                {(() => {
                  console.log('AgentComponent rendering dynamic component:', {
                    dynamicComponent: typeof dynamicComponent,
                    agentId,
                    componentName,
                    token: !!token
                  });
                  
                  try {
                    const componentProps = {
                      agentId: agentId,
                      componentName: componentName,
                      token: token,
                      onClose: () => navigate(`/agents/${agentId}`)
                    };
                    
                    console.log('Creating React element with props:', componentProps);
                    
                    const element = React.createElement(dynamicComponent, componentProps);
                    console.log('Created element:', element);
                    
                    return element;
                  } catch (error) {
                    console.error('Error creating React element:', error);
                    return <div>Error rendering component: {error.message}</div>;
                  }
                })()}
              </div>
            ) : (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-8">
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white mx-auto">
                    <FontAwesomeIcon 
                      icon={iconMapping[component.icon] || faRobot} 
                      className="text-2xl" 
                    />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-2">
                      Full-Page Component
                    </h4>
                    <p className="text-blue-800 dark:text-blue-200 mb-4">
                      This is where the actual <strong>{component.displayName}</strong> component would be rendered.
                      The component will be loaded dynamically from the filesystem and displayed in full-page mode.
                    </p>
                    <div className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg">
                      <FontAwesomeIcon icon={faRobot} className="mr-2" />
                      Component Interface Active
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Component Details */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Type</h4>
                <p className="text-gray-600 dark:text-gray-400">{component.type}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Category</h4>
                <p className="text-gray-600 dark:text-gray-400">{component.category}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Render Mode</h4>
                <p className="text-gray-600 dark:text-gray-400">{component.renderMode}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default AgentComponent;